import React, { useState } from 'react';
import { View, Text, StyleSheet, ActivityIndicator } from 'react-native';

import { useRequestAuditQuery } from '../../hooks/queries/useRequestAuditQuery';
import { RequestAuditEntry } from '../../types/Impersonation';
import { dateFormatting } from '../../utils/dateFormatting';
import { camelToReadable } from '../impersonation/ImpersonationActivitySection';

interface ImpersonationAuditStepProps {
  requestId: number;
  isAdmin: boolean;
}

const VISIBLE_FIELDS_LIMIT = 5;

const formatTimestamp = (dateStr: string): string => {
  const date = dateFormatting.parseInstant(dateStr);
  if (!date) return '';
  const now = new Date();
  const isSameYear =
    dateFormatting.calendarDay(date).year ===
    dateFormatting.calendarDay(now).year;

  const dateStr2 =
    dateFormatting.format(date, {
      locale: 'en-US',
      month: 'short',
      day: 'numeric',
      ...(isSameYear ? {} : { year: 'numeric' }),
    }) ?? '';
  const timeStr =
    dateFormatting.format(date, {
      locale: 'en-US',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }) ?? '';
  return `${dateStr2} at ${timeStr}`;
};

const getActionLabel = (entry: RequestAuditEntry): string => {
  if (entry.changesSummary) {
    return 'Changed';
  }
  if (entry.mode === 'read_only') {
    return 'Viewed (read only)';
  }
  return 'Accessed (no changes)';
};

const parseChangedFields = (summary: string | null): string[] => {
  if (!summary) return [];
  return summary.split(',').filter(Boolean).map(camelToReadable);
};

const ChangesList: React.FC<{ fields: string[] }> = ({ fields }) => {
  const [expanded, setExpanded] = useState(false);

  if (fields.length === 0) return null;

  const visibleFields = expanded
    ? fields
    : fields.slice(0, VISIBLE_FIELDS_LIMIT);
  const hiddenCount = fields.length - VISIBLE_FIELDS_LIMIT;
  const hasMore = hiddenCount > 0;

  return (
    <Text style={styles.changesText}>
      {visibleFields.join(', ')}
      {hasMore && !expanded && (
        <Text style={styles.moreLink} onPress={() => setExpanded(true)}>
          {' +' + hiddenCount + ' more'}
        </Text>
      )}
    </Text>
  );
};

const AuditEntry: React.FC<{ entry: RequestAuditEntry }> = ({ entry }) => {
  const fields = parseChangedFields(entry.changesSummary);
  const actionLabel = getActionLabel(entry);
  const isReadOnly = entry.mode === 'read_only';

  return (
    <View style={styles.entryCard}>
      {/* Line 1: Action headline + name */}
      <View style={styles.entryLine1}>
        <View style={styles.entryHeadlineContainer}>
          <Text style={styles.entryAction}>{actionLabel}</Text>
          {fields.length > 0 && <ChangesList fields={fields} />}
        </View>
        <Text style={styles.entryName}>{entry.impersonatorName}</Text>
      </View>

      {/* Line 2: Timestamp + read-only badge */}
      <View style={styles.entryLine2}>
        <Text style={styles.entryTimestamp}>
          {formatTimestamp(entry.createdAt)}
        </Text>
        {isReadOnly && (
          <View style={styles.readOnlyBadge}>
            <Text style={styles.readOnlyBadgeText}>Read Only</Text>
          </View>
        )}
      </View>
    </View>
  );
};

export const ImpersonationAuditStep: React.FC<ImpersonationAuditStepProps> = ({
  requestId,
  isAdmin,
}) => {
  const { data, isLoading, isError } = useRequestAuditQuery(requestId, isAdmin);

  if (!isAdmin) {
    return null;
  }

  const entries = data?.entries ?? [];

  return (
    <View style={styles.container}>
      <Text style={styles.title}>Impersonation Audit</Text>

      {isLoading && (
        <View style={styles.stateContainer}>
          <ActivityIndicator size="small" color="#6b7280" />
          <Text style={styles.loadingText}>Loading audit entries...</Text>
        </View>
      )}

      {isError && (
        <View style={styles.stateContainer}>
          <Text style={styles.errorText}>
            Failed to load impersonation audit data.
          </Text>
        </View>
      )}

      {!isLoading && !isError && entries.length === 0 && (
        <View style={styles.stateContainer}>
          <Text style={styles.emptyText}>No impersonation activity</Text>
        </View>
      )}

      {!isLoading &&
        !isError &&
        entries.map(entry => <AuditEntry key={entry.auditId} entry={entry} />)}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 16,
  },
  stateContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 24,
    justifyContent: 'center',
  },
  loadingText: {
    fontSize: 14,
    color: '#6b7280',
  },
  errorText: {
    fontSize: 14,
    color: '#dc3545',
  },
  emptyText: {
    fontSize: 14,
    color: '#9ca3af',
    fontStyle: 'italic',
  },
  entryCard: {
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 10,
    marginBottom: 8,
    backgroundColor: '#ffffff',
  },
  entryLine1: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  entryHeadlineContainer: {
    flex: 1,
    marginRight: 12,
  },
  entryAction: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  changesText: {
    fontSize: 13,
    color: '#4b5563',
    marginTop: 2,
  },
  moreLink: {
    color: '#e55a2b',
    fontWeight: '500',
  },
  entryName: {
    fontSize: 13,
    color: '#6b7280',
    flexShrink: 0,
  },
  entryLine2: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  entryTimestamp: {
    fontSize: 12,
    color: '#9ca3af',
  },
  readOnlyBadge: {
    backgroundColor: '#f3f4f6',
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 1,
  },
  readOnlyBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#6b7280',
  },
});
